import { create } from "zustand";

import type { Table, ZoneConfig } from "@/store/types";

/** Staff placing a reservation by hand in a walk-ins-only zone. The backend
 *  allows it — the floor sometimes decides so on purpose — but nothing
 *  automatic ever proposes those zones, so a manual pick deserves a second
 *  look before it goes through. */
export interface WalkInZoneContext {
  zoneNames: string[];
}

interface Pending {
  context: WalkInZoneContext;
  resolve: (confirmed: boolean) => void;
}

interface State {
  pending: Pending | null;
  open: (context: WalkInZoneContext) => Promise<boolean>;
  close: (confirmed: boolean) => void;
}

export const useWalkInZoneConfirmStore = create<State>((set, get) => ({
  pending: null,
  open: (context) =>
    new Promise<boolean>((resolve) => {
      set({ pending: { context, resolve } });
    }),
  close: (confirmed) => {
    const { pending } = get();
    if (pending) {
      pending.resolve(confirmed);
      set({ pending: null });
    }
  },
}));

/** Names of the walk-ins-only zones the given tables sit in. */
export function walkInOnlyZoneNames(
  tableIds: readonly string[],
  tables: readonly Table[],
  zones: readonly ZoneConfig[],
): string[] {
  const zoneIds = new Set(tables.filter((t) => tableIds.includes(t.id)).map((t) => t.zoneId));
  return zones
    .filter((z) => zoneIds.has(z.id) && z.reservationMode === "walk_in_only")
    .map((z) => z.name);
}

/** Resolves ``true`` when the tables are fine for a reservation, or staff
 *  confirmed placing it in a walk-ins-only zone anyway. */
export function confirmReservationTables(
  tableIds: readonly string[],
  tables: readonly Table[],
  zones: readonly ZoneConfig[],
): Promise<boolean> {
  const zoneNames = walkInOnlyZoneNames(tableIds, tables, zones);
  if (zoneNames.length === 0) return Promise.resolve(true);
  return useWalkInZoneConfirmStore.getState().open({ zoneNames });
}
